import { useState } from 'react';
import type { StudentLoginLink } from '@skolveckan/shared';
import { useApi } from '../hooks/useApi';
import { ApiError, api } from '../lib/api';
import { ConfirmDialog } from './ConfirmDialog';
import { IconLink } from './Icons';

/**
 * Lärarens vy över elevernas inloggningslänkar.
 *
 * Länken visas en enda gång, direkt efter att den skapats. Servern sparar bara
 * en hash, så den går inte att läsa igen — precis som ett lösenord. Det är
 * medvetet, och texten i vyn säger det, för annars ser det ut som en bugg.
 */
export function LoginLinks({ classId }: { classId?: string } = {}) {
  const list = useApi(() => api.loginLinks(classId), [classId]);
  const [fresh, setFresh] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<StudentLoginLink | null>(null);

  const students = list.data?.students ?? [];
  if (list.loading || students.length === 0) return null;

  async function create(student: StudentLoginLink) {
    setBusy(student.studentUserId);
    setError(null);
    try {
      const res = await api.createLoginLink(student.studentUserId, classId);
      setFresh((prev) => ({ ...prev, [student.studentUserId]: res.url }));
      list.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kunde inte skapa länken.');
    } finally {
      setBusy(null);
    }
  }

  async function revoke(student: StudentLoginLink) {
    setBusy(student.studentUserId);
    setError(null);
    try {
      await api.revokeLoginLink(student.studentUserId, classId);
      setFresh((prev) => {
        const next = { ...prev };
        delete next[student.studentUserId];
        return next;
      });
      list.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kunde inte återkalla länken.');
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  }

  async function copy(url: string, studentId: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(studentId);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      setError('Kunde inte kopiera. Markera länken och kopiera för hand.');
    }
  }

  return (
    <section className="stack loginlinks">
      <span className="label row" style={{ gap: 7 }}>
        <IconLink size={13} />
        Inloggning utan Google
      </span>
      <p className="muted loginlinks__intro">
        För elever som inte kan använda ett Google-konto. Länken bokmärks en gång på
        elevens dator.
      </p>

      {error && <p className="field__error">{error}</p>}

      {students.map((s) => {
        const url = fresh[s.studentUserId];
        return (
          <div className="loginlinks__row" key={s.studentUserId}>
            <div className="row" style={{ gap: 8 }}>
              <span className="loginlinks__name">{s.studentName}</span>
              <span className="grow" />
              {s.hasLink && !url && (
                <button
                  type="button"
                  className="loginlinks__revoke"
                  onClick={() => setConfirming(s)}
                >
                  Återkalla
                </button>
              )}
              <button
                type="button"
                className="addrow loginlinks__action"
                disabled={busy === s.studentUserId}
                onClick={() => void create(s)}
              >
                {busy === s.studentUserId ? 'Skapar…' : s.hasLink ? 'Ny länk' : 'Skapa länk'}
              </button>
            </div>

            <span className="loginlinks__meta">
              {s.hasLink
                ? s.lastUsedAt
                  ? `Använd senast ${formatWhen(s.lastUsedAt)}`
                  : 'Länk finns, inte använd än'
                : 'Ingen länk'}
            </span>

            {url && (
              <div className="loginlinks__fresh">
                <p className="loginlinks__once">
                  Kopiera nu — länken går inte att läsa igen. Skapa en ny om den kommer bort.
                </p>
                {/* Fältet på egen rad: länken är över hundra tecken och
                    sidokolumnen är smal, så sida vid sida med knappen syns
                    bara "http://" och det ser trasigt ut. */}
                <label className="visually-hidden" htmlFor={`link-${s.studentUserId}`}>
                  Inloggningslänk för {s.studentName}
                </label>
                <input
                  id={`link-${s.studentUserId}`}
                  className="loginlinks__url"
                  type="text"
                  readOnly
                  value={url}
                  onFocus={(e) => e.currentTarget.select()}
                />
                <button
                  type="button"
                  className="button button--ghost loginlinks__copy"
                  onClick={() => void copy(url, s.studentUserId)}
                >
                  {copied === s.studentUserId ? 'Kopierad' : 'Kopiera länken'}
                </button>
              </div>
            )}
          </div>
        );
      })}

      <ConfirmDialog
        open={confirming !== null}
        title="Återkalla inloggningslänken?"
        body={
          confirming
            ? `${confirming.studentName} blir utloggad nästa gång bokmärket används, och du behöver skapa en ny länk.`
            : ''
        }
        cancelLabel="Avbryt"
        confirmLabel="Återkalla"
        onCancel={() => setConfirming(null)}
        onConfirm={() => confirming && void revoke(confirming)}
      />
    </section>
  );
}

function formatWhen(unixSeconds: number): string {
  const d = new Date(unixSeconds * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
